import { BrowserModule } from '@angular/platform-browser';
import { inject, LOCALE_ID, NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import { environment } from '../environments/environment';
import { AppEffects } from './store/app/app.effects';
import { AuthModule } from './routes/auth/auth.module';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoaderService } from './shared/services/loader/loader.service';
import { HttpConfigInterceptor } from '../interceptor/interceptor';
import { OfflinePageComponent } from './shared/components/offline/offline-page.component';
import { OfflineBannerComponent } from './shared/components/offline/offline-banner.component';
import * as AppReducer from './store/app/app.reducer';
import * as CoreReducer from './store/core/core.reducer';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { AuthGuard } from './shared/services/guards/auth.guard';
import { CustomRouterStateSerializer } from './custom-router-state-serializer';
import { InvisibleComponent } from './pages/invisible/invisible.component';
import { SplashScreenComponent } from './pages/splash-screen/splash-screen.component';
import { DatePipe, registerLocaleData } from '@angular/common';
import locale from '@angular/common/locales/en-150';
import { AppSupportComponent } from './pages/app-support/app-support.component';
import { AppPrivacyComponent } from './pages/app-privacy/app-privacy.component';
import { WINDOW_PROVIDERS } from "./window.provider";
import { RouterModule } from '@angular/router';
import { ActionReducer, MetaReducer, Store, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { localStorageSync } from 'ngrx-store-localstorage';
import { provideAppInitializer } from '@angular/core';
import { PlatformService } from './shared/services/platform.service';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { DIALOG_DEFAULT_OPTIONS } from './shared/helpers/dialog-defaults';
import { TokenStorageService } from './shared/services/token-storage.service';
import * as AuthActions from './store/auth/auth.actions';

registerLocaleData(locale);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    `uv.languages.phrases.api`,
    ''
  );
}

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    // 'main' is lazy-loaded and intentionally not fully persisted, but
    // navigationInfo must survive a page reload: detail/dashboard pages
    // read it on init and bail with "Access denied" when it is null.
    // Nested key persists only that field, not the whole (heavy) slice.
    keys: ['app', { main: ['navigationInfo'] }],
    rehydrate: true
  })(reducer);
}

// The auth slice (JWT included) lives in sessionStorage so it survives a
// reload but not the tab: nothing token-related stays on disk indefinitely.
// The whole slice minus transient fields is needed on rehydrate: guards and
// pages read user, domains and roles without re-fetching after a reload.
export function sessionStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    keys: [{ auth: ['currentUser', 'availablesDomains', 'currentDomain', 'currentDomainProperties', 'userRoles', 'jwtToken'] }],
    rehydrate: true,
    storage: sessionStorage
  })(reducer);
}

// Removes the auth state persisted to disk by previous versions.
localStorage.removeItem('auth');

// On native the auth slice lives in the Keychain/Keystore instead, restored by
// the initializer below. Leaving both in place would keep a copy of the token
// in the WebView's storage, which is exactly what secure storage avoids.
const myMetaReducers: Array<MetaReducer<any, any>> = PlatformService.isNativePlatform()
  ? [localStorageSyncReducer]
  : [localStorageSyncReducer, sessionStorageSyncReducer];

@NgModule({
  declarations: [
    AppComponent,
    InvisibleComponent,
    SplashScreenComponent,
    AppSupportComponent,
    AppPrivacyComponent
  ],
  bootstrap: [AppComponent],
  imports: [BrowserModule,
    RouterModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    BrowserAnimationsModule,
    AuthModule,
    SharedModule,
    OfflinePageComponent,
    OfflineBannerComponent,
    StoreModule.forRoot({
      router: routerReducer,
      app: AppReducer.appReducer,
      core: CoreReducer.coreReducer
    },
    {
      metaReducers: myMetaReducers
    }),
    !environment.production ? StoreDevtoolsModule.instrument({
      name: 'Urbanhub App DevTools',
      maxAge: 50,
      logOnly: false,
      connectInZone: true
    }) : [],
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
      serializer: CustomRouterStateSerializer
    }),
    EffectsModule.forRoot([AppEffects])
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: HttpConfigInterceptor, multi: true },
    AuthGuard,
    LoaderService,
    DatePipe,
    { provide: LOCALE_ID, useClass: DatePipe, useValue: 'en-150' },
    WINDOW_PROVIDERS,
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: DIALOG_DEFAULT_OPTIONS },
    provideLottieOptions({
      player: () => player
    }),
    provideHttpClient(withInterceptorsFromDi()),
    // Must finish before the first navigation: the guards read currentUser
    // synchronously, and on native the session arrives asynchronously.
    provideAppInitializer(async () => {
      const tokenStorage = inject(TokenStorageService);
      const store = inject(Store);

      const auth = await tokenStorage.load();

      if (auth?.jwtToken) {
        store.dispatch(AuthActions.sessionRestored(auth));
      }

      tokenStorage.startPersisting();
    }),
  ]
})
export class AppModule { }
